import { useCallback } from "react";

import {
  format,
  formatDistanceStrict,
  FormatDistanceStrictOptions,
  formatDuration,
  FormatDurationOptions,
  intervalToDuration,
} from "date-fns";
import { useTranslation } from "react-i18next";

import useFnsLocale from "./useFnsLocale";

type DateIsh = Date | number | string;

export type FormatterType = (date: DateIsh, formatStr: string) => string;

export function useFormat() {
  const { fnsLocale } = useFnsLocale();
  const dateTimeFormat: FormatterType = useCallback(
    (date, formatStr): string =>
      format(date, formatStr, {
        locale: fnsLocale,
      }),
    [fnsLocale],
  );

  return { format: dateTimeFormat };
}

export function useFormatDistance() {
  const { fnsLocale } = useFnsLocale();

  return useCallback(
    (
      date: DateIsh,
      earlierDate: DateIsh,
      options?: FormatDistanceStrictOptions,
    ): string =>
      formatDistanceStrict(date, earlierDate, {
        locale: fnsLocale,
        ...options,
      }),
    [fnsLocale],
  );
}

export function useFormatDuration() {
  const { fnsLocale } = useFnsLocale();

  return useCallback(
    (start: DateIsh, end: DateIsh, options?: FormatDurationOptions): string => {
      const duration = intervalToDuration({ start, end });

      if (duration.years !== undefined) {
        return formatDuration(duration, {
          locale: fnsLocale,
          format: ["years", "months"],
          ...options,
        });
      }

      if (duration.days !== undefined) {
        return formatDuration(duration, {
          locale: fnsLocale,
          format: ["years", "months", "weeks", "days"],
          ...options,
        });
      }

      if (duration.hours !== undefined) {
        return formatDuration(duration, {
          locale: fnsLocale,
          format: ["years", "months", "weeks", "days", "hours", "minutes"],
          ...options,
        });
      }

      if (duration.minutes !== undefined) {
        return formatDuration(duration, {
          locale: fnsLocale,
          format: ["minutes", "seconds"],
          ...options,
        });
      }

      return formatDuration(duration, {
        locale: fnsLocale,
        ...options,
      });
    },
    [fnsLocale],
  );
}

export function useFormatDurationShort() {
  const { t } = useTranslation();

  return useCallback(
    (durationSeconds: number): string => {
      const hours = Math.floor(durationSeconds / 3_600);
      const remainingSeconds = durationSeconds % 3_600;
      const minutes = Math.floor(remainingSeconds / 60);

      if (hours) {
        return `${hours}${t("h")} ${minutes}${t("m")}`;
      }

      return `${minutes}${t("m")}`;
    },
    [t],
  );
}
